import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
];

export default function LanguageSwitcher({ dark = true, openUp = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const textColor = dark ? 'rgba(255,255,255,0.75)' : '#4A4A5A';
  const borderColor = dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)';
  const bgColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const dropdownBg = dark ? '#12121E' : '#FFFFFF';
  const dropdownBorder = dark ? 'rgba(112,18,128,0.25)' : 'rgba(0,0,0,0.10)';
  const hoverBg = dark ? 'rgba(112,18,128,0.18)' : 'rgba(112,18,128,0.07)';

  return (
    <Box ref={ref} sx={{ position: 'relative', userSelect: 'none' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.7,
          px: 1.4,
          py: 0.6,
          borderRadius: '10px',
          border: `1px solid ${borderColor}`,
          background: bgColor,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(112,18,128,0.5)',
            background: hoverBg,
          },
        }}
      >
        <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{current.flag}</Typography>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: textColor, letterSpacing: 0.3 }}>
          {current.code.toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: textColor, opacity: 0.6, ml: 0.2 }}>
          {open ? '▲' : '▼'}
        </Typography>
      </Box>

      {open && (
        <Box sx={{
          position: 'absolute',
          ...(openUp
            ? { bottom: 'calc(100% + 8px)', left: 0 }
            : { top: 'calc(100% + 8px)', right: 0 }
          ),
          minWidth: 155,
          background: dropdownBg,
          border: `1px solid ${dropdownBorder}`,
          borderRadius: '12px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          zIndex: 999,
          backdropFilter: 'blur(20px)',
        }}>
          {LANGUAGES.map(lang => (
            <Box
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                px: 1.8,
                py: 1,
                cursor: 'pointer',
                background: lang.code === current.code ? hoverBg : 'transparent',
                transition: 'background 0.15s ease',
                '&:hover': { background: hoverBg },
              }}
            >
              <Typography sx={{ fontSize: '1rem' }}>{lang.flag}</Typography>
              <Typography sx={{
                fontSize: '0.82rem',
                color: lang.code === current.code ? '#701280' : textColor,
                fontWeight: lang.code === current.code ? 700 : 400,
              }}>
                {lang.label}
              </Typography>
              {lang.code === current.code && (
                <Typography sx={{ fontSize: '0.65rem', color: '#701280', ml: 'auto' }}>✓</Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
